<template>
  <div class="price-floor-table">
    <h4 class="mb-4">
      {{ $t('Websites') }} {{ bid_type }} {{ $t('Customization') }}
    </h4>

    <p class="mb-4 websites-modal-desc">
      {{ $t('Some of the websites you have targeted will cost more than') }}
      ${{ cost_per_action }} {{ bid_type }},
      {{ $t('You need to edit or confirm these changes to your budget before you can continue.') }}
    </p>

    <p class="mb-4 websites-modal-note">
      {{ $t('*You will not be asked again to confirm this unless there are changes in prices or selected websites. From the website performance section, on the campaign insight page, you can easily update the website bids.') }}
    </p>


    <vs-table :data="domains"> 
      <template slot="thead">
        <vs-th>
        </vs-th>
        <vs-th class="font-medium">
          {{ $t('Platform') }}
        </vs-th>
        <vs-th class="font-medium">
          {{ $t('Type') }}
        </vs-th>
        <vs-th class="font-medium">
          {{ bid_type }}
        </vs-th>
      </template>

      <template slot-scope="{ data }">
        <vs-tr class="tr-border" v-for="(platform, index) in data" :key="'custom-domain-' + index">
          <vs-td class="checkbox-row">
            <div class="modal-checkbox">
              <vs-checkbox id="price-floor-checkbox" :checked="isChecked[platform.id]" @change="togglePlatformSelection(platform)"></vs-checkbox>
            </div>
          </vs-td>

            <vs-td class="platform-row">
              <div class="price-floor-platform">
                {{ platform.title }}
              </div>
            </vs-td>

          <vs-td>
            <div :class="platform.type == 'websites' ? 'website-dot' : 'mobile-application-dot'"></div>
            <div class="price-floor-type">
              {{ platform.type }}
            </div>
          </vs-td>


          <vs-td v-if="!editing.includes(platform.id)">
            <div class="price-floor-edit-icon">
              <span class="underline">${{ custom_bid_value(platform) }}</span>
              <vs-icon id="price-floor-edit-button" v-if="isChecked[platform.id]" @click="allowEditingCustomBid(platform)" class="ml-2 cursor-pointer" style="font-size: 15px;" icon="speakol-edit" icon-pack="speakol"></vs-icon>
            </div>
          </vs-td>

          <vs-td class="actions-row" v-else>
            <div  class="price-floor-signs">
              <vs-row>
                <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-w="6">
                  <vx-input-group>
                    <template slot="prepend">
                      <div class="prepend-text dollar-sign" style="margin-top: 0;">
                        <span>$</span>
                      </div>
                    </template>

                    <div class="vx-input-group-default flex-grow">
                      <div class="vs-component vs-con-input-label vs-input w-full vs-input-primary">
                        <div class="vs-con-input">
                          <input id="price-floor-money-input" class="vs-inputx vs-input--input" min="0" type="number" v-model="bid_editing_values[platform.id]">
                        </div>
                        <span></span>
                      </div>
                    </div>
                  </vx-input-group>
                </vs-col>

                <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-w="6">
                  <vs-icon id="price-floor-input-save-button" class="save-new-cpm" @click="editCustomBid(platform)" style="font-size: 25px;" icon="speakol-save" icon-pack="speakol"></vs-icon>
                  <vs-icon id="price-floor-input-cancel-button" class="cancel-new-cpm" @click="disableEditingCustomBid(platform)" style="font-size: 25px;" icon="speakol-cancel" icon-pack="speakol"></vs-icon>
                </vs-col>
              </vs-row>
            </div>
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>

    <div class="websites-popup-button text-right mt-4 pt-4">
      <vs-button id="price-floor-cancel-button" class="mr-4 cancel-button" color="primary" @click="cancel" type="border">{{ $t('Cancel') }}</vs-button>
      <vs-button id="price-floor-confirm-button" class="confirm-button" color="primary" @click="confirm" type="filled">{{ $t('Confirm') }}</vs-button>
    </div>
  </div>
</template>

<script>
import {cloneDeep} from 'lodash'

export default {
  props: {
    value: {
      required: true,
      type: Array
    },
    domains: {
      type: Array,
      required: true
    },
    action: {
      type: String,
      required: true
    },
    bid_type: {
      type: String,
      required: true
    },
    cost_per_action: {
      required: true
    },
    popupActive: {
      type: Boolean,
      required: true
    },
    cancel: {
      type: Function,
      required: true
    },
    adsetIndex: {
      required: true,
      type: Number
    },
    isEditing: {
      required: false,
      type: Boolean,
      default: false
    },
    bidsConfirmed: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  watch: {
    value: {
      handler (newVal) {
        // a custom bids object for editing
        if (this.isEditing) {
          this.custom_bids_edit = cloneDeep(this.value)
        }

        this.$emit('input', newVal)
      },
      deep: true
    },
    domains () {
      const oldCustomBids = this.value.length ? this.value : []

      this.custom_bids = oldCustomBids

      this.$emit('input', oldCustomBids)

      const combined_domains = this.domains.map(domain => {
        const cindex = oldCustomBids.findIndex(i => i.id === domain.id)
        if (cindex !== -1) {
          domain.newbid = this.bidsConfirmed && +oldCustomBids[cindex].bid > +domain.minbid ? +oldCustomBids[cindex].bid : +domain.newbid
        }

        return domain
      })

      combined_domains.forEach((platform) => {
        this.addCustomBid(platform)
      })
    },
    popupActive () {
      this.editing = []
    }
  },
  data () {
    return {
      editing: [],
      bid_editing_values: {}, // not related to edit campaign
      custom_bids: [],
      custom_bids_edit: []
    }
  },
  created () {
    // a custom bids object for editing
    if (this.isEditing) {
      this.custom_bids_edit = cloneDeep(this.value)
    }

    if (this.value.length) {
      this.custom_bids = this.value
    }
  },
  computed: {
    isChecked () {
      const checkList = {}

      this.domains.forEach((platform) => {
        checkList[platform.id] = false
      })

      this.custom_bids.forEach((platform) => {
        checkList[platform.id] = true
      })

      return checkList
    }
  },
  methods: {
    togglePlatformSelection (platform) {
      const cindex = this.custom_bids.findIndex(i => i.id === platform.id)

      if (cindex !== -1) {
        this.removeCustomBid(platform)
        return
      }

      this.addCustomBid(platform)
    },
    addCustomBid (platform) {
      let bid = platform.newbid ? +platform.newbid : +platform.minbid

      // set the value in editing values
      // which is used in the inputs
      this.bid_editing_values[platform.id] = bid

      const cindex = this.custom_bids.findIndex(i => i.id === platform.id)

      if (cindex === -1) {
        // in case editing the campaign we need to refill from the old custom bid
        const editIndex = this.custom_bids_edit.findIndex(i => i.id === platform.id)
        if (editIndex > -1) {
          const editBidding = parseFloat(this.custom_bids_edit[editIndex].bid).toFixed(3)

          if (editBidding > bid) {
            bid = editBidding
          }
        }

        this.custom_bids.push({
          id: platform.id,
          bid
        })

        // send event to src/views/campaigns/create/steps/components/AdsetContent/components/TargetPlatforms.vue => mounted()
        this.$root.$emit(`price-flooring:${this.adsetIndex}:platform-added`, {
          id: platform.id,
          type: platform.type
        })

        return
      }

      // edit the custom bid if exists
      this.custom_bids[cindex].bid = bid
    },
    removeCustomBid (platform) {
      const cindex = this.custom_bids.findIndex(i => i.id === platform.id)

      if (cindex !== -1) {
        this.custom_bids.splice(cindex, 1)
      }

      // send event to src/views/campaigns/create/steps/components/AdsetContent/components/TargetPlatforms.vue => mounted()
      this.$root.$emit(`price-flooring:${this.adsetIndex}:platform-deleted`, {
        id: platform.id,
        type: platform.type
      })

      this.disableEditingCustomBid(platform)
    },
    allowEditingCustomBid (platform) {
      const cindex = this.editing.findIndex(i => i === platform.id)

      if (cindex !== -1) {
        return
      }

      this.editing.push(platform.id)

      // add a value to the newbid if it is empty
      if (platform.newbid === null || platform.newbid === '' || platform.newbid === 0) {
        platform.newbid = +platform.newbid
      }
    },
    disableEditingCustomBid (platform) {
      const cindex = this.editing.findIndex(i => i === platform.id)

      if (cindex === -1) {
        return
      }

      // reset the input value to it's original bid
      const customBidIndex = this.custom_bids.findIndex(i => i.id === platform.id)
      if (customBidIndex > -1) {
        this.bid_editing_values[platform.id] = this.custom_bids[customBidIndex].bid
      }

      this.editing.splice(cindex, 1)
    },
    editCustomBid (platform) {
      const cindex = this.custom_bids.findIndex(i => i.id === platform.id)

      const value = parseFloat(this.bid_editing_values[platform.id] || 0).toFixed(3)

      if (cindex < 0 || value < parseFloat(platform.minbid)) {
        alert(`The minimum allowed ${  this.bid_type  } for ${  platform.title  } is $${  platform.minbid}`)
        return
      }

      if (value > 9999.999) {
        alert('The value should be less than 9999.999')
        return
      }

      this.custom_bids[cindex].bid = value

      this.disableEditingCustomBid(platform)
    },
    confirm () {
      this.$emit(
        'input',
        cloneDeep(
          this.custom_bids.filter(
            i => this.isChecked.hasOwnProperty(i.id)
          )
        )
      )

      this.cancel()

      this.$emit('confirm')
    },
    custom_bid_value (platform) {
      const custom_newbid = +(this.custom_bids.find(c => c.id === platform.id) || {}).bid || 0,
        platform_minbid = +platform.minbid || 0
      return custom_newbid > platform_minbid ? custom_newbid : platform_minbid
    }
  }
}
</script>

<style lang="scss">


  .checkbox-row,
  .actions-row {
    padding-right: 0px !important;
  }

  .actions-row, 
  .checkbox-row, 
  .platform-row {
    width: 0px;
  }

  .website-dot {
    background-color: #0092d7;
    width: 10px;
    height: 10px;
    float: left;
    margin-right: 5px;
    margin-top: 5px;
    border-radius: 100%;
  }

  .mobile-application-dot {
    background-color: #6dc0ad;
    width: 10px;
    height: 10px;
    float: left;
    margin-right: 5px;
    margin-top: 5px;
    border-radius: 100%;
  }

  .vs-popup--content {
    padding: 30px !important;
  }

  .websites-modal-desc {
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #999;
  }

  .websites-modal-note {
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;

    @apply text-speakol-color;
  }

  .vs-button-primary.vs-button-filled:hover {
    box-shadow: none !important;
  }

  .cancel-button {
    color: black !important;
    border-radius: 3px !important;
    border: 2px solid #d9d9d9 !important;
    padding: 7px 30px !important;
  }

  .cancel-button:hover {
    background: transparent !important;
  }

  .confirm-button {
    background: #009bde !important;
    border-radius: 3px !important;
    color: white !important;
    padding: 8px 40px !important;
  }

  .tr-values:not(.activeEdit):not(.tr-expandedx):not(.hoverFlat):hover {
    transform: none !important;
  }

  .tr-border {
    border-bottom: 1px solid rgba(189, 195, 202, 0.215527);
    height: 60px;
  }

  .holamundo {
    .vs-con-table .vs-con-tbody {
      height: 200px;
      overflow-y: scroll;
      overflow-x: hidden;
    }

    .vs-table--tbody-table tr:first-child {
      background: rgba(0, 146, 215, 0.06) !important;
    }
  }

  .save-new-cpm {
    color: #34b576;
    font-size: 13px !important;
    background: rgba(244, 244, 244, 0.745629);
    border-radius: 4px;
    cursor: pointer;
    margin-right: 10px;
    padding: 5px;
  }

  .cancel-new-cpm {
    color: #e02020;
    font-size: 13px !important;
    background: rgba(244, 244, 244, 0.745629);
    border-radius: 4px;
    cursor: pointer;
    padding: 5px;
  }

  .modal-checkbox {
    .vs-checkbox--input:checked + .vs-checkbox .vs-icon {
      font-size: 12px !important;
      font-weight: bolder !important;
    }

    .vs-checkbox-primary input:checked + .vs-checkbox {
      width: 16px;
      height: 16px;
    }

    .vs-checkbox-primary .vs-checkbox {
      width: 16px;
      height: 16px;
    }
  }

  .price-floor-platform {
    text-overflow: ellipsis;
    overflow: hidden;
    width: 280px;
    white-space: nowrap;
  }

  .price-floor-input {
    .vs-input--input.normal {
      width: 90px;
    }
  }

  .price-floor-table {
    .vs-con-tbody {
      overflow-x: auto !important;
    }
  }

  .price-floor-signs {
    width: 250px;
  }

  .price-floor-type {
    width: 78px;
  }

  @media (max-width: 576px) {
    .vs-con-table .vs-con-tbody {
      overflow-x: scroll !important;
      width: 176%;
    }

    .price-floor-platform {
      text-overflow: ellipsis;
      width: 130px;
      overflow: hidden;
      white-space: nowrap;
    }
  }
</style>
